import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import DocumentFilters from '@/components/DocumentFilters';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FileText, Clock, Shield, Eye, Link as LinkIcon, Download, FileUp, Share2, Send, Trash2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ChainVisualization from '@/components/ChainVisualization';
import { copyToClipboard } from '@/utils/clipboard';
import { API } from '@/config/api';

export default function DocumentsPage({ token, user, onLogout }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chainDialog, setChainDialog] = useState(false);
  const [chain, setChain] = useState([]);
  const [chainUdtId, setChainUdtId] = useState(null);
  const [shareDialog, setShareDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [expiryUnit, setExpiryUnit] = useState('days');
  const [expiryValue, setExpiryValue] = useState(1);
  const [sharePermissions, setSharePermissions] = useState('view_only');
  const [allowReshare, setAllowReshare] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDocuments();
    
    // Check for hash in URL
    const hash = window.location.hash;
    if (hash === '#api') {
      setTimeout(() => {
        const apiSection = document.querySelector('[data-section="api-documents"]');
        if (apiSection) {
          apiSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents);
      setFilteredDocuments(response.data.documents);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters) => {
    let filtered = [...documents];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.file_name.toLowerCase().includes(searchLower) ||
        doc.udt_id.toLowerCase().includes(searchLower) ||
        (doc.purpose && doc.purpose.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(doc => filters.status.includes(doc.status));
    }

    // Source filter
    if (filters.source.length > 0) {
      filtered = filtered.filter(doc => {
        const isApi = doc.purpose?.includes('Fetched from') || doc.purpose?.includes('API');
        if (filters.source.includes('api') && isApi) return true;
        if (filters.source.includes('manual') && !isApi) return true;
        return false;
      });
    }

    // API Source filter
    if (filters.apiSource.length > 0) {
      filtered = filtered.filter(doc => {
        if (!doc.purpose) return false;
        return filters.apiSource.some(source => doc.purpose.includes(source));
      });
    }

    // File type filter
    if (filters.fileType.length > 0) {
      filtered = filtered.filter(doc => filters.fileType.includes(doc.file_type));
    }

    // Created date filter
    if (filters.dateFrom || filters.dateTo) {
      filtered = filtered.filter(doc => {
        const createdDate = new Date(doc.created_at);
        const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : new Date(0);
        const toDate = filters.dateTo ? new Date(filters.dateTo) : new Date();
        return createdDate >= fromDate && createdDate <= toDate;
      });
    }

    // Expiry date filter
    if (filters.expiryFrom || filters.expiryTo) {
      filtered = filtered.filter(doc => {
        const expiryDate = new Date(doc.expiry);
        const fromDate = filters.expiryFrom ? new Date(filters.expiryFrom) : new Date(0);
        const toDate = filters.expiryTo ? new Date(filters.expiryTo) : new Date('2099-12-31');
        return expiryDate >= fromDate && expiryDate <= toDate;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name-asc':
          return a.file_name.localeCompare(b.file_name);
        case 'name-desc':
          return b.file_name.localeCompare(a.file_name);
        case 'expiry-soon':
          return new Date(a.expiry) - new Date(b.expiry);
        case 'expiry-later':
          return new Date(b.expiry) - new Date(a.expiry);
        default:
          return 0;
      }
    });

    setFilteredDocuments(filtered);
  };

  const viewChain = async (udtId) => {
    if (!udtId) return;
    try {
      setChainUdtId(udtId);
      const response = await axios.get(`${API}/chain/${udtId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChain(response.data.chain || []);
      setChainDialog(true);
    } catch (error) {
      toast.error('Failed to fetch chain');
    }
  };

  const refreshChain = () => {
    if (chainUdtId) viewChain(chainUdtId);
  };

  const revokeAccessFromChain = async (recipientId) => {
    if (!chainUdtId || !recipientId) return;
    const confirmed = window.confirm('Revoke access for this recipient? They will no longer be able to open this document.');
    if (!confirmed) return;

    try {
      await axios.post(
        `${API}/access/revoke/${chainUdtId}?recipient_id=${encodeURIComponent(recipientId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Access revoked for selected recipient');
      refreshChain();
      // Also refresh main list so status/counts stay in sync
      fetchDocuments();
    } catch (error) {
      console.error('Revoke access error:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const copyUDTId = async (udtId) => {
    const link = `${window.location.origin}/request/${udtId}`;
    await copyToClipboard(link, 'Request link copied!');
  };

  const openShareDialog = (doc) => {
    if (doc.status === 'expired') {
      toast.error('Cannot share an expired document');
      return;
    }
    setSelectedDocument(doc);
    setShareDialog(true);
    setRecipientQuery('');
    setSearchResults([]);
    setSelectedRecipient(null);
    setShareMessage('');
    setExpiryUnit('days');
    setExpiryValue(1);
    setAllowReshare(false);
  };

  const computedExpiryHours = expiryUnit === 'days' ? Math.min(365, Math.max(1, expiryValue)) * 24 : Math.min(8760, Math.max(1, expiryValue));

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/chat/users/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.users || []);
    } catch (error) {
      setSearchResults([]);
    }
  };

  const shareDocument = async () => {
    if (!selectedRecipient?.user_id) {
      toast.error('Please search and select a recipient');
      return;
    }

    try {
      setSharing(true);

      // Share the document
      await axios.post(
        `${API}/share/direct`,
        {
          udt_id: selectedDocument.udt_id,
          recipient_id: selectedRecipient.user_id,
          message: shareMessage || `Shared: ${selectedDocument.file_name}`,
          expiry_hours: computedExpiryHours,
          permissions: sharePermissions || 'view_only',
          allow_reshare: !!allowReshare
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Document shared successfully with ${selectedRecipient.name}!`);
      setShareDialog(false);
      setSelectedDocument(null);
      setRecipientQuery('');
      setSearchResults([]);
      setSelectedRecipient(null);
      setShareMessage('');
    } catch (error) {
      console.error('Share error:', error);
      let errorMessage = 'Failed to share document';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle both string and array/object error formats
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setSharing(false);
    }
  };

  const openDeleteDialog = (doc) => {
    setDocumentToDelete(doc);
    setDeleteDialog(true);
  };

  const deleteDocument = async () => {
    try {
      setDeleting(true);
      await axios.delete(`${API}/documents/${documentToDelete.udt_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Document deleted successfully');
      setDeleteDialog(false);
      setDocumentToDelete(null);
      
      // Refresh document list
      fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const exportPDF = async (udtId) => {
    try {
      const response = await axios.get(`${API}/documents/${udtId}/audit-report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `UDT_Audit_${udtId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit report downloaded');
    } catch (error) {
      toast.error('Failed to export audit report');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const apiDocs = filteredDocuments.filter(doc => doc.purpose?.includes('Fetched from') || doc.purpose?.includes('API'));
  const manualDocs = filteredDocuments.filter(doc => !doc.purpose?.includes('Fetched from') && !doc.purpose?.includes('API'));
  
  const [stats, setStats] = useState({ documents: 0 });
  
  useEffect(() => {
    setStats({ documents: documents.length });
  }, [documents]);

  return (
    <DashboardLayout user={user} token={token} onLogout={onLogout} stats={stats}>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Documents</h1>
            <p className="text-gray-600 text-sm">View and manage all your secure documents</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              onClick={() => navigate('/dashboard/upload')}
              style={{ background: '#3b82f6' }}
              className="w-full sm:w-auto"
            >
              <FileUp className="w-4 h-4 mr-2" />
              Upload New
            </Button>
            <Button
              onClick={() => navigate('/dashboard/fetch-docs')}
              style={{ background: '#10b981' }}
              className="w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Fetch from APIs
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 sm:mb-6">
          <DocumentFilters
            documents={documents}
            onFilterChange={handleFilterChange}
            totalCount={documents.length}
            filteredCount={filteredDocuments.length}
          />
        </div>

        <div className="space-y-8">
          {/* API Fetched Documents Section */}
          {apiDocs.length > 0 && (
            <div data-section="api-documents">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                <Download className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: '#10b981' }} />
                <h2 className="text-lg sm:text-2xl font-bold" style={{ color: '#10b981' }}>
                  📥 API Fetched Documents
                </h2>
                <Badge style={{ background: '#10b981' }} className="text-xs">
                  {apiDocs.length} {apiDocs.length === 1 ? 'Document' : 'Documents'}
                </Badge>
              </div>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {apiDocs.map((doc) => (
                  <Card 
                    key={doc.udt_id} 
                    className="hover-lift flex flex-col"
                    style={{ borderColor: '#10b981', borderWidth: '2px' }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Download className="w-10 h-10" style={{ color: '#10b981' }} />
                          <Badge style={{ background: '#10b981', fontSize: '10px' }}>API</Badge>
                        </div>
                        <div className="flex gap-1">
                          {doc.status && (
                            <Badge className={getStatusColor(doc.status)}>
                              {doc.status}
                            </Badge>
                          )}
                          {typeof doc.violation_count === 'number' && doc.violation_count > 0 && (
                            <Badge className="bg-orange-100 text-orange-800">
                              {doc.violation_count}{' '}
                              {doc.violation_count === 1 ? 'violation' : 'violations'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CardTitle className="text-lg mt-2">{doc.file_name}</CardTitle>
                      <CardDescription>UDT ID: {doc.udt_id}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4" style={{ color: '#666' }} />
                        <span style={{ color: '#666' }}>
                          Expires: {new Date(doc.expiry).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Shield className="w-4 h-4" style={{ color: '#666' }} />
                        <span style={{ color: '#666' }}>Chain: {doc.watermark_chain.length} hops</span>
                      </div>
                      {doc.purpose && (
                        <div className="space-y-1">
                          <p className="text-sm font-semibold" style={{ color: '#10b981' }}>
                            📥 {doc.purpose.split(' - ')[0]}
                          </p>
                          {doc.purpose.includes(' - ') && (
                            <p className="text-xs px-2 py-1 rounded" style={{ background: '#f0fdf4', color: '#166534' }}>
                              Source: {doc.purpose.split(' - ')[1]}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-4">
                        <Button
                          size="sm"
                          onClick={() => openShareDialog(doc)}
                          style={{ background: doc.status === 'expired' ? '#9ca3af' : '#10b981' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                          disabled={doc.status === 'expired'}
                          title={doc.status === 'expired' ? 'Cannot share expired document' : 'Share'}
                        >
                          <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Share
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/dashboard/documents/view/${doc.udt_id}`)}
                          style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Open
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyUDTId(doc.udt_id)}
                          style={{ borderColor: '#10b981', color: '#10b981' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <LinkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewChain(doc.udt_id)}
                          style={{ borderColor: '#10b981', color: '#10b981' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Chain
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => exportPDF(doc.udt_id)}
                          style={{ borderColor: '#10b981', color: '#10b981' }}
                          className="flex-shrink-0 text-xs sm:text-sm hidden sm:inline-flex"
                        >
                          📄 PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteDialog(doc)}
                          style={{ borderColor: '#ef4444', color: '#ef4444' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Manually Uploaded Documents Section */}
          {manualDocs.length > 0 && (
            <div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                <FileUp className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: '#0F52BA' }} />
                <h2 className="text-lg sm:text-2xl font-bold" style={{ color: '#0F52BA' }}>
                  📤 Manually Uploaded Documents
                </h2>
                <Badge style={{ background: '#0F52BA' }} className="text-xs">
                  {manualDocs.length} {manualDocs.length === 1 ? 'Document' : 'Documents'}
                </Badge>
              </div>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {manualDocs.map((doc) => (
                  <Card key={doc.udt_id} className="hover-lift flex flex-col">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0" style={{ color: '#0F52BA' }} />
                          <Badge variant="outline" style={{ fontSize: '10px' }}>Manual</Badge>
                        </div>
                        <div className="flex gap-1">
                          {doc.status && (
                            <Badge className={getStatusColor(doc.status)}>
                              {doc.status}
                            </Badge>
                          )}
                          {typeof doc.violation_count === 'number' && doc.violation_count > 0 && (
                            <Badge className="bg-orange-100 text-orange-800">
                              {doc.violation_count}{' '}
                              {doc.violation_count === 1 ? 'violation' : 'violations'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CardTitle className="text-base sm:text-lg mt-2 break-words">{doc.file_name}</CardTitle>
                      <CardDescription className="break-all">UDT ID: {doc.udt_id}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 flex-shrink-0" style={{ color: '#666' }} />
                        <span className="min-w-0" style={{ color: '#666' }}>
                          Expires: {new Date(doc.expiry).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Shield className="w-4 h-4" style={{ color: '#666' }} />
                        <span style={{ color: '#666' }}>Chain: {doc.watermark_chain.length} hops</span>
                      </div>
                      {doc.purpose && (
                        <div className="space-y-1 min-w-0">
                          <p className="text-sm font-semibold break-words" style={{ color: '#0F52BA' }}>
                            Type: {doc.purpose.split(' - ')[0]}
                          </p>
                          {doc.purpose.includes(' - ') && (
                            <p className="text-sm break-words" style={{ color: '#666' }}>
                              Notes: {doc.purpose.split(' - ')[1]}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-4">
                        <Button
                          size="sm"
                          onClick={() => openShareDialog(doc)}
                          style={{ background: doc.status === 'expired' ? '#9ca3af' : '#0F52BA' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                          disabled={doc.status === 'expired'}
                          title={doc.status === 'expired' ? 'Cannot share expired document' : 'Share'}
                        >
                          <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Share
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/dashboard/documents/view/${doc.udt_id}`)}
                          style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Open
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyUDTId(doc.udt_id)}
                          style={{ borderColor: '#0F52BA', color: '#0F52BA' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <LinkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewChain(doc.udt_id)}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Chain
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => exportPDF(doc.udt_id)}
                          style={{ borderColor: '#10b981', color: '#10b981' }}
                          className="flex-shrink-0 text-xs sm:text-sm hidden sm:inline-flex"
                        >
                          📄 PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteDialog(doc)}
                          style={{ borderColor: '#ef4444', color: '#ef4444' }}
                          className="flex-shrink-0 text-xs sm:text-sm"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}


          {/* Empty State */}
          {filteredDocuments.length === 0 && !loading && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
              <p className="text-lg mb-2" style={{ color: '#666' }}>
                {documents.length === 0 ? 'No documents yet' : 'No documents match your filters'}
              </p>
              <p className="text-sm mb-4" style={{ color: '#999' }}>
                {documents.length === 0 
                  ? 'Upload documents manually or fetch from Government APIs'
                  : 'Try adjusting your search criteria or filters'
                }
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate('/dashboard/upload')}
                  style={{ background: '#0F52BA' }}
                >
                  <FileUp className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
                <Button
                  onClick={() => navigate('/dashboard/fetch-docs')}
                  style={{ background: '#10b981' }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Fetch from APIs
                </Button>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4" style={{ color: '#666' }}>Loading documents...</p>
            </div>
          )}
        </div>
      </div>

      {/* Chain Dialog */}
      <Dialog open={chainDialog} onOpenChange={(open) => { setChainDialog(open); if (!open) setChainUdtId(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <DialogTitle className="text-base sm:text-lg">Document Custody Chain</DialogTitle>
                <DialogDescription className="text-xs sm:text-sm">Complete watermark chain visualization</DialogDescription>
              </div>
              <Button variant="outline" size="sm" onClick={refreshChain} disabled={!chainUdtId} className="flex-shrink-0">
                Refresh
              </Button>
            </div>
          </DialogHeader>
          <ChainVisualization
            chain={chain}
            udtId={chainUdtId}
            canRevoke={!!(chainUdtId && documents.some((d) => d.udt_id === chainUdtId))}
            ownerId={user?.user_id}
            onRevokeRecipient={revokeAccessFromChain}
          />
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)]">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Share2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: '#0F52BA' }} />
              Share Document
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm break-words">
              Share "{selectedDocument?.file_name}" securely with another user
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[70vh] overflow-y-auto space-y-4 py-4 pr-1 min-w-0">
            <div className="space-y-2">
              <Label className="text-sm">Recipient (mobile / email / name / UDT User ID)</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Search recipient…"
                  value={recipientQuery}
                  onChange={(e) => {
                    setRecipientQuery(e.target.value);
                    setSelectedRecipient(null);
                  }}
                  className="flex-1 min-w-0"
                />
                <Button
                  variant="outline"
                  onClick={() => searchUsers(recipientQuery)}
                  title="Search"
                  className="sm:flex-shrink-0"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Enter mobile number, email, name, or UDT User ID, then click search and pick from the list.
              </p>
              {searchResults.length > 0 && !selectedRecipient && (
                <div className="border rounded max-h-56 overflow-y-auto divide-y bg-white">
                  {searchResults.map((u) => (
                    <button
                      type="button"
                      key={u.user_id}
                      onClick={() => setSelectedRecipient(u)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="font-semibold">{u.name}</p>
                          <p className="text-xs text-gray-600 break-all">{u.email}</p>
                        </div>
                        {u.udt_user_id && (
                          <Badge variant="outline" className="font-mono text-xs">
                            {u.udt_user_id}
                          </Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedRecipient && (
                <div className="p-3 rounded border bg-gray-50 flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{selectedRecipient.name}</p>
                    <p className="text-xs text-gray-600 break-all">{selectedRecipient.email}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setSelectedRecipient(null)}>
                    Change
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="share-message">Message (Optional)</Label>
              <Input
                id="share-message"
                placeholder="Add a message for the recipient..."
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Access Expires In</Label>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="number"
                  min="1"
                  max={expiryUnit === 'days' ? 365 : 8760}
                  value={expiryValue}
                  onChange={(e) => setExpiryValue(parseInt(e.target.value) || (expiryUnit === 'days' ? 1 : 24))}
                  className="w-full min-w-0 sm:w-28"
                />
                <select
                  value={expiryUnit}
                  onChange={(e) => {
                    setExpiryUnit(e.target.value);
                    if (e.target.value === 'days' && expiryValue > 365) setExpiryValue(365);
                    if (e.target.value === 'hours' && expiryValue > 8760) setExpiryValue(8760);
                  }}
                  className="w-full sm:w-auto min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="days">Days</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
              <p className="text-xs text-gray-500">
                The recipient will have access for {expiryUnit === 'days'
                  ? `${expiryValue} day${expiryValue !== 1 ? 's' : ''} (${expiryValue * 24} hours)`
                  : `${expiryValue} hour${expiryValue !== 1 ? 's' : ''}`}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="permissions" className="text-sm">Permissions *</Label>
              <select
                id="permissions"
                value={sharePermissions || 'view_only'}
                onChange={(e) => setSharePermissions(e.target.value)}
                className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="view_only">View Only</option>
                <option value="download_view">Download and View</option>
                <option value="print_download_view">Print, Download and View</option>
              </select>
              <p className="text-xs text-gray-500">
                {sharePermissions === 'view_only' && 'Recipient can only view the document'}
                {sharePermissions === 'download_view' && 'Recipient can view and download the document'}
                {sharePermissions === 'print_download_view' && 'Recipient can view, download, and print the document'}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Reshare Permission</Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={allowReshare}
                  onChange={(e) => setAllowReshare(e.target.checked)}
                />
                <span>Allow recipient to reshare (forward) this document</span>
              </label>
              <p className="text-xs text-gray-500">
                If enabled, the recipient will see a “Reshare” button on their received shares.
              </p>
            </div>

            <div className="bg-blue-50 p-3 rounded-md text-sm min-w-0">
              <p className="font-semibold text-blue-900 mb-1">📋 How to find UDT User ID:</p>
              <ul className="text-blue-800 space-y-1 text-xs ml-4 list-disc break-words">
                <li>Ask the recipient to check their Dashboard</li>
                <li>The UDT User ID is displayed at the top of their dashboard</li>
                <li>Format: UDT-USER-XXXXXX (6 characters)</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShareDialog(false)}
              disabled={sharing}
            >
              Cancel
            </Button>
            <Button
              onClick={shareDocument}
              disabled={sharing || !selectedRecipient}
              style={{ background: '#0F52BA' }}
            >
              {sharing ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Sharing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Share Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Document?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{documentToDelete?.file_name}"</strong>?
              <br /><br />
              This action cannot be undone. This will permanently delete:
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>The document and its encrypted file</li>
                <li>All access tokens and shares</li>
                <li>Misuse evidence records</li>
                <li>Document custody chain</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteDocument}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>Delete Document</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
